import gql from 'graphql-tag';

// Alert severity from the server schema (matches AlertSeverityType enum)
export type AlertSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

// Alert criticality for alerts dropdown (matches schema)
export type AlertCriticality = 'INFO' | 'WARNING' | 'CRITICAL';

// User read record for alerts
export interface AlertUserRead {
	alertUserReadId: string;
	userId: string;
	readAt: string;
}

// Alert view model for alerts list (matches AlertViewModel from schema)
export interface AlertViewModel {
	alertId: string;
	facilityId: string;
	criticality: AlertCriticality;
	code: string;
	duration: number | null;
	payload: string | null;
	createdAt: string;
	userReads?: AlertUserRead[];
}

// Alert created payload from subscription (matches AlertCreatedPayload type in schema)
export interface AlertCreatedPayload {
	alertId: string;
	facilityId: string;
	severity: AlertSeverity;
	title: string | null;
	message: string | null;
	source: string | null;
	relatedEntityId: string | null;
	relatedEntityType: string | null;
	createdAt: string;
}

// Legacy type alias for backward compatibility
export type MaintenanceEventPayload = AlertCreatedPayload;

// GraphQL subscription for alert created events (real-time alerts)
export const ON_ALERT_CREATED_SUBSCRIPTION = gql`
	subscription OnAlertCreated {
		onAlertCreated {
			alertId
			facilityId
			severity
			title
			message
			source
			relatedEntityId
			relatedEntityType
			createdAt
		}
	}
`;

// Legacy export for backward compatibility
export const MAINTENANCE_FEED_SUBSCRIPTION = ON_ALERT_CREATED_SUBSCRIPTION;

// Query for alerts list
export const ALERTS_LIST_QUERY = gql`
	query AlertsList(
		$first: Int
		$after: String
		$where: AlertViewModelFilterInput
		$order: [AlertViewModelSortInput!]
	) {
		alerts(first: $first, after: $after, where: $where, order: $order) {
			nodes {
				alertId
				facilityId
				criticality
				code
				duration
				payload
				createdAt
			}
			pageInfo {
				hasNextPage
				endCursor
			}
			totalCount
		}
	}
`;

// Query for single alert
export const ALERT_QUERY = gql`
	query Alert($alertId: Ulid!) {
		alert(alertId: $alertId) {
			alertId
			facilityId
			criticality
			code
			duration
			payload
			createdAt
			userReads {
				alertUserReadId
				userId
				readAt
			}
		}
	}
`;

// Query for unread alerts
export const UNREAD_ALERTS_QUERY = gql`
	query UnreadAlerts(
		$userId: Ulid!
		$first: Int
		$after: String
		$where: AlertViewModelFilterInput
		$order: [AlertViewModelSortInput!]
	) {
		unreadAlerts(userId: $userId, first: $first, after: $after, where: $where, order: $order) {
			nodes {
				alertId
				facilityId
				criticality
				code
				duration
				payload
				createdAt
			}
			pageInfo {
				hasNextPage
				endCursor
			}
			totalCount
		}
	}
`;

// Query for critical alerts
export const CRITICAL_ALERTS_QUERY = gql`
	query CriticalAlerts(
		$first: Int
		$after: String
		$order: [AlertViewModelSortInput!]
	) {
		criticalAlerts(first: $first, after: $after, order: $order) {
			nodes {
				alertId
				facilityId
				criticality
				code
				duration
				payload
				createdAt
			}
			pageInfo {
				hasNextPage
				endCursor
			}
			totalCount
		}
	}
`;

// Legacy exports for backward compatibility
export const ALERTS_QUERY = ALERTS_LIST_QUERY;
export const MAINTENANCE_SNAPSHOT_QUERY = ALERTS_LIST_QUERY;
